import { useState, useEffect, useContext } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, ClickAwayListener, Paper, Popper, Typography } from "@mui/material";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import "../../styles/common.css"
import React from "react";
import { useLocation } from "react-router-dom";
import { UserInfo } from "../../utils/types";
import CommonService from "../../services/common/commonService.ts";
import OpenTaskIcon from "../../assets/common/Open-task-icon.svg";
import CloseTaskIcon from "../../assets/common/Close-task-icon.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UserTeamIcon from "../../assets/common/people_icon.svg";
import FilledUserTeamIcon from "../../assets/common/people_icon_filled.svg";
import { GlobalContext } from "../../context/GlobalContext.tsx";

type TreeNode = UserInfo & { children?: TreeNode[] };

const getAncestors = (
  employeePorId: string,
  allUsers: UserInfo[],
  managerOptions: UserInfo[]
): string[] => {
  const ancestors: string[] = [];
  let currentId = employeePorId;
  const userMap = new Map<string, string>();
  allUsers.forEach((u) => {
    if (u.employeePorId && u.managerPorId) {
      userMap.set(u.employeePorId, u.managerPorId);
    }
  });
  managerOptions.forEach((u) => {
    if (u.employeePorId && u.managerPorId) {
      userMap.set(u.employeePorId, u.managerPorId);
    }
  });

  const visited = new Set<string>();
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const managerId = userMap.get(currentId);
    if (managerId) {
      ancestors.push(managerId);
      currentId = managerId;
    } else {
      break;
    }
  }
  return ancestors;
};

const UserTeam = ({
  setDepartment,
  setSelectedUser,
  loggedInEmail,
  loggedInProfileOrgId,
  showDropdown = false,
  display,
  onDataUpdate = () => {},
  parent_page,
  showDomainFirst = false,
  selectedEmployeePorId,
}: {
  setDepartment?: React.Dispatch<React.SetStateAction<string>>;
  setSelectedUser: React.Dispatch<React.SetStateAction<any | null>>;
  loggedInProfileOrgId?: string;
  loggedInEmail?: string;
  showDropdown?: boolean;
  display: boolean;
  showDomainFirst?: boolean;
  onDataUpdate?: (data: {
    currentUser: string;
    clickedUserInfo?: UserInfo;
    currentManager?: UserInfo;
    departments: any[];
    subordinates: UserInfo[];
    managerOptions: UserInfo[];
  }) => void;
  parent_page?: string;
  selectedEmployeePorId?: string;
}) => {
   const location = useLocation();
  const newUserTeamData = location.state?.newUserTeamData;
  const { showSnackbar } = useDisplaySnackbar();
  const [departments, setDepartments] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<string>("")
  const [hierarchyQueryUser, setHierarchyQueryUser] = useState<string>("");
  const [subordinates, setSubordinates] = useState<UserInfo[]>([]);
  const [managerOptions, setManagerOptions] = useState<UserInfo[]>([]);
  const [currentManager, setCurrentManager] = useState<UserInfo  | undefined>();
  const [clickedUserInfo, setClickedUserInfo] = useState<UserInfo>();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const popperRef = React.useRef<HTMLDivElement | null>(null);
  const hierarchyUsersMapRef = React.useRef<Map<string, UserInfo>>(new Map());
  const [allHierarchyUsers, setAllHierarchyUsers] = useState<UserInfo[]>([]);
  const [loadingNodeId, setLoadingNodeId] = useState<string | null>(null);
  const getDescendantIds = (nodeId: string): string[] => {
    const map = new Map<string, TreeNode>();

    allHierarchyUsers.forEach(user => {
      map.set(user.employeePorId, { ...user, children: [] });
    });

    // build tree relationships
    map.forEach(node => {
      if (node.managerPorId && map.has(node.managerPorId)) {
        map.get(node.managerPorId)!.children!.push(node);
      }
    });

    const collect = (id: string): string[] => {
      const node = map.get(id);
      if (!node || !node.children) return [];
      return node.children.flatMap(child => [
        child.employeePorId,
        ...collect(child.employeePorId),
      ]);
    };

    return collect(nodeId);
  };

  const toggleNode = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        // collapse node + all descendants
        const descendants = getDescendantIds(id);
        next.delete(id);
        descendants.forEach(d => next.delete(d));
      } else {
        // expand only this node
        next.add(id);
      }
      return next;
    });
  };

  const handleTogglePopper = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(prev => !prev);
  };

  const handleClosePopper = () => {
    setOpen(false);
  };
  useEffect(() => {
    if (loggedInProfileOrgId) {
      setCurrentUser(loggedInProfileOrgId);
      setHierarchyQueryUser(loggedInProfileOrgId);
    }
  }, []);

  useEffect(() => {
    if (!selectedEmployeePorId) return;
    if (
      currentUser === selectedEmployeePorId &&
      hierarchyQueryUser === selectedEmployeePorId
    ) {
      return;
    }

    setCurrentUser(selectedEmployeePorId);
    setHierarchyQueryUser(selectedEmployeePorId);
    setSelectedValue(selectedEmployeePorId);
  }, [selectedEmployeePorId, currentUser, hierarchyQueryUser]);
  const [selectedValue, setSelectedValue] = useState<string | null>(
    showDomainFirst ? "domainOption" : null
  );
  useEffect(() => {
    if (showDomainFirst) {
      setCurrentUser("");
    }
  }, [showDomainFirst]);
  const selectedOption =
  selectedValue
    ? selectedValue
    : showDomainFirst && managerOptions.length > 0
    ? "domainOption"
    : currentUser;

  const { impersonationDetails } = useContext(GlobalContext);

  const activeEmployeePorId =
    impersonationDetails?.accessType === "team"
      ? impersonationDetails.impersonatingUserPorId
      : loggedInProfileOrgId;

  const hierarchyEmployeePorId = hierarchyQueryUser;

  useEffect(() => {
    if (!activeEmployeePorId) return;

    setCurrentUser(activeEmployeePorId);
    setHierarchyQueryUser(activeEmployeePorId);

    hierarchyUsersMapRef.current.clear();
    setAllHierarchyUsers([]);
    setManagerOptions([]);
    setSubordinates([]);
  }, [activeEmployeePorId]);

  const {
          isLoading: isHierarchyLoading,
          isFetching: isHierarchyFetching,
          isSuccess: isHierarchySuccess,
          isError: isHierarchyError,
          error: hierarchyError,
          data: hierarchyData,   // Extracted hierarchy data
      } = CommonService.useGetHierarchyInfo(
          {
              employeePorId: hierarchyEmployeePorId || "",
              depthLevel: 2,
          },
          !!hierarchyEmployeePorId
      );

      useEffect(() => {
      
          if (isHierarchySuccess) {
              const hierarchyResult = Array.isArray(hierarchyData?.data) ? hierarchyData.data : [];
              if (hierarchyResult.length > 0) {
                  // Map hierarchy data to UserInfo format
                  const extendedUserInfo: UserInfo[] = hierarchyResult.map((user: any) => ({
                    id: user.id,
                    email: user.employeeEmail,
                    name: user.name || "",
                    picture_url: user.pictureUrl || null,
                    empDept: user.empDept || "",
                    managerId: user.managerId || "",
                    managerName: user.managerName || "",
                    managerEmail: user.managerEmail || "",
                    employeePorId: user.employeePorId || "",
                    managerPorId: user.managerPorId || "",
                  }));
                  // Keep previously fetched hierarchy nodes so top levels stay visible in popper.
                  extendedUserInfo.forEach((user) => {
                    hierarchyUsersMapRef.current.set(user.employeePorId, user);
                  });
                  const mergedHierarchyUsers = Array.from(
                    hierarchyUsersMapRef.current.values()
                  );
                  setAllHierarchyUsers(mergedHierarchyUsers);

                  // Separate current user and subordinates from merged hierarchy.
                  const selectedUserInfo = mergedHierarchyUsers.find(
                    (user) => user.employeePorId === hierarchyQueryUser
                  );
  
                  const subordinateInfo = mergedHierarchyUsers.filter(
                    (user) => user.managerPorId === hierarchyQueryUser
                  );
                  if (selectedUserInfo) {
                      setManagerOptions((prev) => {
                        const userMap = new Map<string, UserInfo>();
                        prev.forEach((user) => userMap.set(user.employeePorId, user));
                        [selectedUserInfo, ...subordinateInfo].forEach((user) =>
                          userMap.set(user.employeePorId, user)
                        );
                        return Array.from(userMap.values());
                      });
                      if (hierarchyQueryUser === currentUser) {
                        setCurrentManager(selectedUserInfo);
                      }
                  }
  
                  // Set subordinates
                  setSubordinates(subordinateInfo);
              }
            setLoadingNodeId(null);
          }
  
          if (isHierarchyError) {
              showSnackbar("error", hierarchyError?.message || "Failed to fetch hierarchy data");
              console.error("Error:", hierarchyError);
              setLoadingNodeId(null);
          }
      }, [
          isHierarchyLoading,
          isHierarchyFetching,
          isHierarchySuccess,
          isHierarchyError,
          hierarchyData,
          hierarchyQueryUser,
          currentUser,
      ]);
  useEffect(() => {
    onDataUpdate({
      currentUser,
      clickedUserInfo,
      currentManager,
      departments,
      subordinates,
      managerOptions,
    });
  }, [currentUser, clickedUserInfo, currentManager, departments, subordinates, managerOptions]);

  useEffect(() => {
    if (newUserTeamData) {
      if (newUserTeamData.currentUser) {
        setCurrentUser(newUserTeamData.currentUser);
      }
      if (newUserTeamData.clickedUserInfo) {
        setClickedUserInfo(newUserTeamData.clickedUserInfo);
      }
      if (newUserTeamData.currentManager) {
        setCurrentManager(newUserTeamData.currentManager);
      }

      if (newUserTeamData.departments) {
        setDepartments(newUserTeamData.departments);
      }
      if (newUserTeamData.subordinates) {
        setSubordinates(newUserTeamData.subordinates);
      }
      if (newUserTeamData.managerOptions) {
        setManagerOptions(newUserTeamData.managerOptions);
      }
    }
  }, [newUserTeamData]);
  

  useEffect(() => {
    if(setDepartment)
    departments.length > 0 && setDepartment(departments?.[0]);
  }, [departments]);

  const handleUserSelect = (employeePorId: string) => {
    if (showDomainFirst && employeePorId === "domainOption") {
        // Domain option chosen
        setCurrentUser(""); // no email
        setHierarchyQueryUser("");
        setSelectedUser(null);
        setClickedUserInfo(undefined);
        setCurrentManager(undefined);
        setSelectedValue("domainOption");
        setSubordinates([]);
        setManagerOptions([]);
        hierarchyUsersMapRef.current.clear();
        setAllHierarchyUsers([]);
        return;
      }
    
      setSelectedValue(employeePorId);
    const clickedUser =
      allHierarchyUsers.find((user) => user.employeePorId === employeePorId) ||
      managerOptions.find((user) => user.employeePorId === employeePorId);
    if (clickedUser) {
        setCurrentUser(clickedUser.employeePorId);
        setHierarchyQueryUser(clickedUser.employeePorId);
        setSelectedUser(clickedUser);
        setClickedUserInfo(undefined);
        setCurrentManager(clickedUser);
        setOpen(true);
        setSubordinates([]);
      }
};

const getInitials = (name: string): string => {
    const nameParts = name.split(" ");
    if (nameParts.length === 1) {
        // If there's only one name part, return it as is
        return nameParts[0];
    }
    const firstName = nameParts[0]; // Full first name
    const lastNameInitial = nameParts[nameParts.length - 1][0]?.toUpperCase(); // First letter of the last name
    return `${firstName} ${lastNameInitial}`; // Combine first name and last name initial
};
  useEffect(() => {
    if (open && currentUser) {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.add(currentUser);
        const ancestors = getAncestors(currentUser, allHierarchyUsers, managerOptions);
        ancestors.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [open, currentUser, allHierarchyUsers, managerOptions]);

  useEffect(() => {
    if (open && currentUser) {
      const timer = setTimeout(() => {
        const selectedEl = popperRef.current?.querySelector(
          ".hierarchy-node-row--selected"
        );
        if (selectedEl) {
          selectedEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [open, currentUser]);

const buildHierarchyTree = (
  users: UserInfo[],
  rootId: string
): TreeNode[] => {
  const map = new Map<string, TreeNode>();

  users.forEach(user => {
    map.set(user.employeePorId, { ...user, children: [] });
  });

  // attach children
  map.forEach(node => {
    if (node.managerPorId && map.has(node.managerPorId)) {
      map.get(node.managerPorId)!.children!.push(node);
    }
  });

  map.forEach(node => {
    node.children?.sort((a, b) =>
      (a.name || a.email || "").localeCompare(
        b.name || b.email || "",
        undefined,
        { sensitivity: "base" }
      )
    );
  });
  // return ONLY the logged-in user as root
  const rootNode = map.get(rootId);
  return rootNode ? [rootNode] : [];
};

const HierarchyAccordion = ({
  nodes,
  level = 1,
  isInSelectedTree = false,
}: {
  nodes: TreeNode[];
  level?: number;
  isInSelectedTree?: boolean;
}) => {
  if (!nodes.length) return null;

  return (
    <>
      {nodes.map((node, index) => {
        const isDomainNode = node.employeePorId === "domainOption";
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = isDomainNode || expandedIds.has(node.employeePorId);
        const isSelected = selectedOption === node.employeePorId;
        const isSelectedSubtreeRoot = isSelected && hasChildren && isExpanded;
        const shouldHighlightDescendants = isInSelectedTree || isSelectedSubtreeRoot;
        const shouldHighlightRowConnector = isSelected || isInSelectedTree;
        const memberCount = node.children?.length ?? 0;
        const isLastChild = index === nodes.length - 1;
        const showLoader = 
          loadingNodeId === node.managerPorId &&
          (isHierarchyLoading || isHierarchyFetching);
        return (
          <Accordion
            key={node.employeePorId}
            className={`hierarchy-accordion hierarchy-accordion-node
              ${level > 1 ? "hierarchy-accordion-node--child" : ""}
              ${
                isSelectedSubtreeRoot
                  ? "hierarchy-accordion--selected-subtree"
                  : ""
              }
              ${
                isInSelectedTree
                  ? "hierarchy-accordion-node--selected-tree-line"
                  : ""
              }
              ${
                isLastChild
                  ? "hierarchy-accordion-node--last"
                  : ""
              }
            `}
            disableGutters
            elevation={0}
            defaultExpanded={level === 1}
            expanded={isExpanded}
            sx={{
              "--tree-indent": `${(level - 1) * 20}px`,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              className="hierarchy-accordion-summary"
              expandIcon={null}
            >
              <Box
                className={`hierarchy-node-row ${
                  isSelected ? "hierarchy-node-row--selected" : ""
                } ${isExpanded ? "hierarchy-node-row--expanded" : ""} ${
                  level > 1 ? "hierarchy-node-row--child" : ""
                } ${!hasChildren ? "hierarchy-node-row--child--hasNoChild" : ""}
                 ${
                  shouldHighlightRowConnector ? "hierarchy-node-row--selected-tree-line" : ""
                }`}
                sx={{
                  "--tree-indent": `${(level - 1) * 20}px`,
                  display: "flex",
                  alignItems: "center",
                  pl: "var(--tree-indent)",
                  py: 0.5,
                  width: "100%",
                }}
              >
                {hasChildren ? (
                  <Box
                    className={`hierarchy-toggle-icon ${
                      isDomainNode || isExpanded ? "manager_name_icon--open" : ""
                    } ${isSelected ? "manager_name_icon--selected" : ""}`}
                    sx={{
                      mr: 1,
                      cursor: isDomainNode ? "default" : "pointer",
                    }}
                    onClick={(e) => {
                      if (isDomainNode) return;
                      e.stopPropagation();
                      if (!expandedIds.has(node.employeePorId)) {
                        setLoadingNodeId(node.employeePorId);
                        setHierarchyQueryUser(node.employeePorId);
                      } else {
                        setLoadingNodeId(null);
                      }
                      toggleNode(node.employeePorId);
                    }}
                  >
                    <Box
                      component="img"
                      src={isExpanded ? CloseTaskIcon : OpenTaskIcon}
                      alt="toggle"
                      className="manager_name_icon"
                    />
                  </Box>
                ) : (
                  <Box className="hierarchy-toggle-spacer" sx={{ mr: 1 }} />
                )}
                <Box
                  className="hierarchy_avatar"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserSelect(node.employeePorId);
                  }}
                >
                  {hasChildren ? isSelected ? <img src={FilledUserTeamIcon} /> : <img src={UserTeamIcon}/> : 
                    (
                      node.name
                        ?.split(" ")
                        .map((word: string) => word[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()
                    )
                  }
                </Box>
                <Box
                  className="hierarchy-node-text"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserSelect(node.employeePorId);
                  }}
                >
                  <Typography className="hierarchy-node-name">
                    {node.name || node.email}
                  </Typography>
                  {showLoader ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CircularProgress size={18} />
                    </Box>
                  ) : (
                    memberCount > 0 && (
                      <Typography className="hierarchy-node-count">
                        {memberCount} {memberCount === 1 ? "member" : "members"}
                      </Typography>
                    )
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            {hasChildren && (
              <AccordionDetails
                className={`hierarchy-accordion-details ${
                  shouldHighlightDescendants
                    ? "hierarchy-accordion-details--selected-tree-line"
                    : ""
                }`}
                sx={{
                  "--tree-indent": `${(level - 1) * 20}px`,
                  p: 0,
                }}
              >
                <HierarchyAccordion
                  nodes={node.children!}
                  level={level + 1}
                  isInSelectedTree={shouldHighlightDescendants}
                />
              </AccordionDetails>
            )}
          </Accordion>
        );
      })}
    </>
  );
};

const selectedUserInfo =
  allHierarchyUsers.find(u => u.employeePorId === currentUser) ||
  managerOptions.find(u => u.employeePorId === currentUser);

const rootName = showDomainFirst && !currentUser
  ? (loggedInEmail?.split("@")[1] || "Domain")
  : selectedUserInfo?.name ||
    selectedUserInfo?.email ||
    loggedInEmail?.split("@")[0] ||
    "User";

  const isLoggedInUser = currentUser === loggedInProfileOrgId;
const hasTeam = subordinates.length > 0;

  return (
    <>
            <div
                className="flex flex-col"
                style={{ maxWidth: "100%", overflow: "hidden",
                height: showDomainFirst ? "48px" : "",
             }}
            >
                
                <Box
                    className="flex flex-row justify-start items-start"
                    sx={{
                        position: "relative",
                        // overflowX: "scroll",
                        transition: "max-width 0.20s ease-out",
                    }}
                >
                    {managerOptions.length > 0 &&  (
                        <div className="Manger__name__box">
                            {
                                display && (
                                    <h1 className="Manager__name">Manager</h1>
                                )
                            }
                            <div>
                            {showDropdown && (
                                <>
                                <Box className="manager_name_box"
                                  onClick={handleTogglePopper}
                                >
                                    <Typography className="manager_name_text">
                                      {showDomainFirst
                                        ? rootName
                                        : isLoggedInUser
                                          ? (parent_page === "account-page"
                                          ? `${rootName} & team`
                                          : `${rootName}'s team`)
                                          : hasTeam
                                          ? (parent_page === "account-page" || parent_page === "analytics-page"
                                              ? `${rootName} & team`
                                              : `${rootName}'s team`)
                                            : rootName}
                                    </Typography>
                                    <KeyboardArrowDownIcon
                                      fontSize="small"
                                      className="fp-arrow visible"
                                      sx={{marginLeft: 1}}
                                    />
                                </Box>
                                {/* Popper */}
                                <Popper
                                    open={open}
                                    anchorEl={anchorEl}
                                    placement="bottom-start"
                                    style={{ zIndex: 1300 }}
                                >
                                    <ClickAwayListener
                                      onClickAway={(event) => {
                                        if (popperRef.current?.contains(event.target as Node)) return;
                                        if (anchorEl?.contains(event.target as Node)) return;
                                        handleClosePopper();
                                      }}
                                    >
                                    <Paper
                                        ref={popperRef}
                                        data-parent-page={parent_page}
                                        className="user-accordion-container"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {
                                          <HierarchyAccordion
                                            nodes={buildHierarchyTree(
                                              allHierarchyUsers,
                                              activeEmployeePorId || ""
                                            )}
                                            level={1}
                                          />
                                        }
                                    </Paper>
                                    </ClickAwayListener>
                                </Popper>
                                </>
                            )}
                            </div>
                            <div className="Container_names" style={{ marginBottom: showDomainFirst ? "0px" : "" }} >
                                <Typography className="subordinates__name">
                                    {currentManager && clickedUserInfo && display && (
                                        <>
                                            {currentManager.name && (
                                                <>
                                                <span
                                                style={{ cursor: "pointer" }}
                                                onClick={() => handleUserSelect(currentManager.employeePorId)}
                                                onMouseEnter={(e) => (e.currentTarget.style.color = "#5731C3")}
                                                onMouseLeave={(e) => (e.currentTarget.style.color = "#6F6F7F")}
                                                >
                                                {getInitials(currentManager.name)}
                                                
                                                </span>
                                                <span style={{ margin: "0 8px" }}> &gt; </span>
                                                </>
                                            )}
                                            {clickedUserInfo.name && (
                                                <span style={{ color: "purple", fontWeight: "bold" }}>
                                                    {getInitials(clickedUserInfo.name)}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Typography>
                            </div>
                        </div>
                    )}
                </Box>
            </div>
        </>
  );
};

export default UserTeam;
